import {
  type RefObject,
  type UIEvent,
  useCallback,
  useEffect,
  useRef,
} from 'react'

const SCROLL_LOAD_MORE_THRESHOLD_PX = 48

type UseCrmIntegrationChatsListInfiniteScrollParams = {
  viewportRef: RefObject<HTMLDivElement | null>
  chatsCount: number
  hasMore: boolean
  loading: boolean
  loadingMore: boolean
  loadMore: () => void | Promise<void>
}

/** Подгрузка следующей страницы списка CRM-чатов при скролле вниз. */
export const useCrmIntegrationChatsListInfiniteScroll = ({
  viewportRef,
  chatsCount,
  hasMore,
  loading,
  loadingMore,
  loadMore,
}: UseCrmIntegrationChatsListInfiniteScrollParams) => {
  const reachTriggeredRef = useRef(false)
  const loadMoreRef = useRef(loadMore)
  loadMoreRef.current = loadMore

  useEffect(() => {
    if (!loadingMore) {
      reachTriggeredRef.current = false
    }
  }, [loadingMore])

  const tryLoadMore = useCallback(() => {
    if (!hasMore || loading || loadingMore || reachTriggeredRef.current) {
      return
    }

    reachTriggeredRef.current = true
    void loadMoreRef.current()
  }, [hasMore, loading, loadingMore])

  const handleViewportScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (!hasMore || loading || loadingMore) {
        return
      }

      const target = event.currentTarget
      const distanceToBottom =
        target.scrollHeight - (target.scrollTop + target.clientHeight)

      if (distanceToBottom <= SCROLL_LOAD_MORE_THRESHOLD_PX) {
        tryLoadMore()
      }
    },
    [hasMore, loading, loadingMore, tryLoadMore],
  )

  useEffect(() => {
    if (!hasMore || loading || loadingMore || chatsCount === 0) {
      return
    }

    const viewport = viewportRef.current
    if (!viewport) {
      return
    }

    if (viewport.scrollHeight <= viewport.clientHeight + SCROLL_LOAD_MORE_THRESHOLD_PX) {
      tryLoadMore()
    }
  }, [chatsCount, hasMore, loading, loadingMore, tryLoadMore, viewportRef])

  return { handleViewportScroll }
}
